import { EPlatform } from '@/utils/enums/platform-enum';
import { env } from '@/utils/env';
import { isInZeloolApp } from '@/utils/tools';
import { ApplovinProvider } from './providers/applovinProvider';
// import { ArmsProvider } from './providers/armsProvider';
import { AwinProvider } from './providers/awinProvider';
import { AwsWafProvider } from './providers/awsWafProvider';
import { BingProvider } from './providers/bingProvider';
import { ConsentManagerProvider } from './providers/consentManagerProvider';
import { CriteoProvider } from './providers/criteoProvider';
import { EmarsysProvider } from './providers/emarsysProvider';
import { FacebookProvider } from './providers/facebookProvider';
import { Ga4Provider } from './providers/ga4Provider';
import { GoogleBadgeProvider } from './providers/googleBadgeProvider';
import { GoogleTranslateProvider } from './providers/googleTranslateProvider';
import { ImpactRadiusProvider } from './providers/impactProvider';
import { OneTrustProvider } from './providers/oneTrustProvider';
import { PepperjamProvider } from './providers/pepperjamProvider';
import { RtbHouseProvider } from './providers/rtbHouseProvider';
import { SensorsProvider } from './providers/sensorProvider';
import { SnapchatProvider } from './providers/snapchatProvider';
import { SnapchatConversionsProvider } from './providers/snapchatConversionsProvider';
import { TiktokProvider } from './providers/tiktokProvider';
import { TtdPixelProvider } from './providers/ttdPixelProvider';
import { TwitterProvider } from './providers/twitterProvider';
import { ReportingCenter } from './reportingCenter';
import { IS_DE_FR } from '@/utils/constants';
// Export the types and main class
export * from './types';
export { ReportingCenter };

// Initialize reporting on app startup
export async function initializeReporting(deviceType: EPlatform): Promise<ReportingCenter> {
  const center = ReportingCenter.getInstance();
  const isInZeloolAppFlag = isInZeloolApp();

  // Register all providers, but control enable status via environment variables

  // Sensors Provider - Always enabled for analytics
  center.registerProvider(
    new SensorsProvider({
      serverUrl: env.NEXT_PUBLIC_SENSORS_SERVER_URL,
      showLog: env.NEXT_PUBLIC_SENSORS_SHOW_LOG,
      platform: deviceType === EPlatform.WEB ? 'Web' : 'Wap',
      platformName:
        deviceType === EPlatform.WEB
          ? (env.NEXT_PUBLIC_SENSORS_PUB_PLATFORM_NAME ?? 'zeeloolWeb')
          : (env.NEXT_PUBLIC_SENSORS_PUB_WAP_PLATFORM_NAME ?? 'zeeloolWap'),
      envName: env.NEXT_PUBLIC_ENV === 'local' ? 'dev' : env.NEXT_PUBLIC_ENV,
      isEnabled: true, // Sensors is always enabled for analytics
    }),
  );

  // OneTrust Provider，顺序放在前面，尽量在GA4之前就加载好
  center.registerProvider(
    new OneTrustProvider({
      domainScript: 'ad7e7fb7-fa0f-4d1c-8f54-d32280690460',
      isEnabled: env.NEXT_PUBLIC_ENABLE_ONETRUST === 'true' && !isInZeloolAppFlag,
    }),
  );

  center.registerProvider(
    new ConsentManagerProvider({
      cdid: env.NEXT_PUBLIC_CONSENT_MANAGER_CDID,
      isEnabled: env.NEXT_PUBLIC_ENABLE_CONSENT_MANAGER === 'true' && !isInZeloolAppFlag,
    }),
  );

  // Emarsys Provider
  center.registerProvider(
    new EmarsysProvider({
      isEnabled: env.NEXT_PUBLIC_ENABLE_EMARSYS === 'true' && !isInZeloolAppFlag,
      domainCode: env.NEXT_PUBLIC_EMARSYS_DOMAIN_CODE,
      defaultNotificationTitle: env.NEXT_PUBLIC_EMARSYS_DEFAULT_NOTIFICATION_TITLE,
      defaultNotificationIcon: env.NEXT_PUBLIC_EMARSYS_DEFAULT_NOTIFICATION_ICON,
      applicationServerPublicKey: env.NEXT_PUBLIC_EMARSYS_APPLICATION_SERVER_PUBLIC_KEY,
      scarabJsApiUrl: env.NEXT_PUBLIC_EMARSYS_SCARAB_JS_API_URL,
    }),
  );

  // Impact Radius Provider
  center.registerProvider(
    new ImpactRadiusProvider({
      isEnabled: env.NEXT_PUBLIC_ENABLE_IMPACT_RADIUS === 'true',
    }),
  );

  // Awin Provider
  center.registerProvider(
    new AwinProvider({
      merchantId: env.NEXT_PUBLIC_AWIN_MERCHANT,
      isEnabled: env.NEXT_PUBLIC_ENABLE_AWIN === 'true',
    }),
  );

  // AppLovin Provider
  center.registerProvider(
    new ApplovinProvider({
      eventKey: env.NEXT_PUBLIC_APPLOVIN_KEY,
      isEnabled: env.NEXT_PUBLIC_ENABLE_APPLOVIN === 'true',
    }),
  );

  // Bing Provider
  center.registerProvider(
    new BingProvider({
      trackingId: env.NEXT_PUBLIC_BING_ID,
      isEnabled: env.NEXT_PUBLIC_ENABLE_BING === 'true' && !isInZeloolAppFlag,
    }),
  );

  // Criteo Provider
  center.registerProvider(
    new CriteoProvider({
      account: Number(env.NEXT_PUBLIC_CRITEO_ID),
      isEnabled: env.NEXT_PUBLIC_ENABLE_CRITEO === 'true',
    }),
  );

  // Facebook Provider
  center.registerProvider(
    new FacebookProvider({
      pixelId: env.NEXT_PUBLIC_FACEBOOK_ID,
      isEnabled: env.NEXT_PUBLIC_ENABLE_FACEBOOK === 'true' && !isInZeloolAppFlag,
    }),
  );

  // GA4 Provider
  center.registerProvider(
    new Ga4Provider({
      measurementId: env.NEXT_PUBLIC_GA4_MEASUREMENT_ID,
      adId1: env.NEXT_PUBLIC_GA4_AD_ID1,
      adId2: env.NEXT_PUBLIC_GA4_AD_ID2,
      isEnabled: env.NEXT_PUBLIC_ENABLE_GA4 === 'true',
    }),
  );

  // Pepperjam Provider
  center.registerProvider(
    new PepperjamProvider({
      containerId: env.NEXT_PUBLIC_PEPPERJAM_ID,
      isEnabled: env.NEXT_PUBLIC_ENABLE_PEPPERJAM === 'true',
    }),
  );

  // Snapchat Provider
  center.registerProvider(
    new SnapchatProvider({
      appKey: env.NEXT_PUBLIC_SNAPCHAT_KEY,
      isEnabled: env.NEXT_PUBLIC_ENABLE_SNAPCHAT === 'true',
    }),
  );

  /**
   * 作为 SnapchatProvider 补充信号
   * Snapchat Conversions Provider
   * 任务诉求: https://devops.aliyun.com/projex/project/ce85697916dd382a20b8106373/request/49e830ab4fb965ccb3c977a55b
   * 详细参数文档: https://developers.snap.com/api/marketing-api/Conversions-API/Parameters
   */
  center.registerProvider(
    new SnapchatConversionsProvider({
      isEnabled: env.NEXT_PUBLIC_ENABLE_SNAPCHAT_CONVERSION === 'true',
      platform: deviceType === EPlatform.WEB ? 'WEB' : 'MOBILE_APP',
    }),
  );

  // TikTok Provider
  center.registerProvider(
    new TiktokProvider({
      pixelId: env.NEXT_PUBLIC_TIKTOK_PIXEL_KEY,
      isEnabled: env.NEXT_PUBLIC_ENABLE_TT_PIXEL === 'true' && !isInZeloolAppFlag,
    }),
  );

  // TTD Pixel Provider
  center.registerProvider(
    new TtdPixelProvider({
      advertiserId: env.NEXT_PUBLIC_TTDPIXEL_KEY,
      trackingTagIds: env.NEXT_PUBLIC_TTDPIXEL_TAG_IDS,
      isEnabled: env.NEXT_PUBLIC_ENABLE_TTDPIXEL === 'true',
    }),
  );

  // Twitter Provider
  center.registerProvider(
    new TwitterProvider({
      pixelIds: env.NEXT_PUBLIC_TWITTER_KEYS || [],
      isEnabled: env.NEXT_PUBLIC_ENABLE_TWITTER === 'true',
    }),
  );
  // RTB House Provider
  center.registerProvider(
    new RtbHouseProvider({
      trackingId: env.NEXT_PUBLIC_RTB_HOUSE_ID,
      isEnabled: env.NEXT_PUBLIC_ENABLE_RTB_HOUSE === 'true',
      dataCenter: env.NEXT_PUBLIC_RTB_HOUSE_DATA_CENTER,
    }),
  );

  // ARMS Provider - 阿里云用户体验监控
  // 已经迁移到 src/components/Arms/index.tsx 中使用 NPM 包接入
  // center.registerProvider(
  //   new ArmsProvider({
  //     pid: env.NEXT_PUBLIC_ARMS_PID || '',
  //     endpoint: env.NEXT_PUBLIC_ARMS_ENDPOINT || '',
  //     environment: env.NEXT_PUBLIC_ARMS_ENV || 'prod',
  //     isEnabled:
  //       env.NEXT_PUBLIC_ENABLE_ARMS === 'true' &&
  //       !!env.NEXT_PUBLIC_ARMS_PID &&
  //       !!env.NEXT_PUBLIC_ARMS_ENDPOINT &&
  //       !isInZeloolAppFlag,
  //   }),
  // );

  // Google Translate Provider
  center.registerProvider(
    new GoogleTranslateProvider({
      isEnabled: env.NEXT_PUBLIC_ENABLE_GOOGLE_TRANSLATE === 'true' && !isInZeloolAppFlag,
    }),
  );

  // AWS WAF Provider
  center.registerProvider(
    new AwsWafProvider({
      challengeSdkUrl: env.NEXT_PUBLIC_AWS_WAF_CHALLENGE_SDK_URL || '',
      captchaSdkUrl: env.NEXT_PUBLIC_AWS_WAF_CAPTCHA_SDK_URL || '',
      isEnabled: env.NEXT_PUBLIC_ENABLE_AWS_WAF === 'true',
    }),
  );
  // Google Badge Provider - 只在WEB端显示
  // todo 现在只包含老的谷歌徽章，德法要使用新的谷歌徽章
  center.registerProvider(
    new GoogleBadgeProvider({
      merchantId: env.NEXT_PUBLIC_GOOGLE_BADGE_ID,
      isEnabled: deviceType === EPlatform.WEB && !IS_DE_FR, // 内部会根据deviceType和Lighthouse测试自动判断
    }),
  );

  // Initialize all providers (only enabled ones will actually initialize)
  await center.initialize();

  return center;
}
